import { extInfo } from 'lib/extInfo';
import { partition } from 'ramda';
import { ROOT_FOLDER_ID } from 'reactApp/constants/magicIdentificators';
import { getFeatureCreatePublicAlbum } from 'reactApp/modules/features/features.selectors';
import { getCurrentRouteId, getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { RootState } from 'reactApp/store';
import { BreadcrumbsListItem } from 'reactApp/ui/BreadcrumbsContainer/BreadcrumbsContainer';
import { createSelector } from 'reselect';

import { isAlbumAllowedExtension } from './albums.helpers';
import { AlbumItem, IAlbum, IAlbumsState, isAlbum } from './albums.types';

const getAlbumsState = (state: RootState): IAlbumsState => state.albums;

export const getAlbumList = (state: RootState) => getAlbumsState(state).list;

export const getAlbumsItemById = createSelector(
    getAlbumList,
    (state, id) => id,
    (list, id): AlbumItem | undefined => list[id]
);

export const getAlbumsIds = createSelector(getAlbumList, (list): string[] => {
    const item = list[ROOT_FOLDER_ID];

    return isAlbum(item) ? item?.childs || [] : [];
});

export const isCreatePublicAlbumsEnabled = (state, items): boolean => {
    if (!items?.length) {
        return false;
    }

    const currentStorage = getCurrentStorage(state);

    if (currentStorage === EStorageType.albums) {
        return false;
    }

    if (!getFeatureCreatePublicAlbum(state)) {
        return false;
    }

    return items.length > 1 && items.every((item) => isAlbumAllowedExtension(item.ext));
};

export const getAlbumsList = createSelector(getAlbumList, getAlbumsIds, (list, ids) => {
    const items = ids.map((id) => list[id]).filter(Boolean) || [];
    const [weblink, personal] = partition((item) => Boolean(isAlbum(item) && item.weblink), items) as IAlbum[][];

    return {
        personal,
        weblink,
    };
});

export const getCurrentAlbum = createSelector(getCurrentRouteId, getAlbumList, getCurrentStorage, (routeId, list, storage) => {
    if (storage !== EStorageType.albums) {
        return undefined;
    }

    return list[routeId] as IAlbum;
});

export const getCurrentAlbumList = createSelector(getCurrentAlbum, (album) => (isAlbum(album) ? album?.childs || [] : []));

export const isAlbumsLoading = createSelector(getCurrentAlbum, (album): boolean =>
    album ? Boolean(isAlbum(album) && !album?.isLoaded && album?.isLoading) : true
);

export const getAlbumCursor = createSelector(getCurrentAlbum, (album): string | undefined => (isAlbum(album) ? album?.cursor : undefined));

export const isRootAlbumStorage = createSelector(getCurrentAlbum, (album): boolean =>
    isAlbum(album) ? album?.id === ROOT_FOLDER_ID : false
);

export const getAlbumsBreadcrumbs = createSelector(getCurrentAlbum, (album) => {
    const result: BreadcrumbsListItem[] = [
        {
            name: 'Альбомы',
            id: '/albums',
            kind: 'storage' as const,
            storage: EStorageType.albums,
        },
    ];

    if (album && album.id !== ROOT_FOLDER_ID) {
        result.push({
            name: album.name,
            id: `/albums/${album.id}`,
            storage: EStorageType.albums,
            kind: 'folder',
        });
    }

    return result;
});

export const getAlbumAllowedExtensions = (): string[] => extInfo.getExtsForAlbums();
